// Removed usePlayer, AuthContext, and UserSettingsModal from TranscriptionView
import { useBranchingCanvas } from "@/hooks/useBranchingCanvas";
import useCombinedTranscriptions from "@/hooks/useCombinedTranscriptions";
import {
  type Conversation,
  type ConversationMessage,
  useConversationHistory,
} from "@/hooks/useConversationHistory";
import { useFlashcards } from "@/hooks/useFlashcards";
import { useInteractiveWidget } from "@/hooks/useInteractiveWidget";
import { useLearningModeTransition } from "@/hooks/useLearningModeTransition";
import { AgentConnectionState } from "@/hooks/useLiveKitAgent";
import useSpeakingMemoryCapture from "@/hooks/useSpeakingMemoryCapture";
import { useTranscriptPracticeActivity } from "@/hooks/useTranscriptPracticeActivity";
import { useTranscriptTranslationSettings } from "@/hooks/useTranscriptTranslationSettings";
import { useTtsRuntimeControls } from "@/hooks/useTtsRuntimeControls";
import {
  buildTutorReplayMessageIds,
  tutorReplayMessageToken,
  tutorReplayOccurrence,
  useTutorReplay,
} from "@/hooks/useTutorReplay";
import {
  type TutorTranscriptSegment,
  hasFinalizedTutorTurn,
  useTutorTurnTranslations,
} from "@/hooks/useTutorTurnTranslations";
import { useTypedTranscriptMessages } from "@/hooks/useTypedTranscriptMessages";
import {
  type VoiceDemoRecoverySurface,
  useVoiceDemoRecoveryTelemetry,
} from "@/hooks/useVoiceDemoRecoveryTelemetry";
import { useVoiceNoteTurn } from "@/hooks/useVoiceNoteTurn";
import { useWhiteboard } from "@/hooks/useWhiteboard";
import { useWhiteboardPracticeSelection } from "@/hooks/useWhiteboardPracticeSelection";
import { shouldShowDevUserTranscripts } from "@/lib/devUserTranscripts";
import { extendedInteractionState } from "@/lib/extendedInteractionState";
import { createLogger } from "@/lib/logger";
import {
  type OnboardingConversationMilestones,
  getOnboardingConversationMilestones,
} from "@/lib/onboarding/demo-telemetry";
import type { SessionMode } from "@/lib/sessionRouting";
import { supabase } from "@/lib/supabaseClient";
import { buildSessionArtifactMessages } from "@/lib/transcriptSessionArtifacts";
import {
  getTranslationLanguageName,
  resolveSentenceMapLanguageCode,
} from "@/lib/translation-language";
import { splitTypedTranscriptDisplay, transcriptReplaySource } from "@/lib/typedTranscriptDisplay";
import { resolveVoiceSessionRecoveryKind } from "@/lib/voice-session-recovery";
import type { WhiteboardBoardResult } from "@/lib/whiteboard/boardResults";
import { shouldRouteExerciseToTutorWhiteboard } from "@/lib/whiteboard/exerciseRouting";
import { getWhiteboardSpeechObservationId } from "@/lib/whiteboard/speechObservation";
import { useRoomContext, useVoiceAssistant } from "@livekit/components-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  BookOpen,
  Check,
  Copy,
  Loader2,
  MoreHorizontal,
  Presentation,
  Sparkles,
  Square,
  Volume2,
} from "lucide-react";
import dynamic from "next/dynamic";
import * as React from "react";
import { useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import languages from "../data/languages.json";
import type { SessionAgentState } from "../shared/lesson/view-contracts";
import { useAuth } from "./AuthContext";
// Import flashcard components and hooks
import {
  type FlashcardData,
  FlashcardTooltip,
  SelectableText,
  type WordActionAnchor,
  WordActionBar,
} from "./flashcard";
// Import PlusOptionsMenu for mobile-friendly session summary
import { PlusOptionsMenu } from "./practice-chat/PlusOptionsMenu";
// Import modular TranscriptionView components
import {
  AudioTrackError,
  ConnectionStatus,
  ContinuationContext,
  EmptyState,
  LearningModeTransitionControl,
  PronunciationAnalysis,
  ScrollToBottomButton,
  SessionHeader,
  TranscriptionMessage,
  TranslationControls,
  TutorSpeedDial,
  VoiceNoteComposer,
} from "./transcription-view";
import { TranscriptTextInput } from "./transcription-view/TranscriptTextInput";
import workspaceStyles from "./transcription-view/TranscriptWorkspace.module.css";
import { WhiteboardPracticeSelectionNotice } from "./transcription-view/WhiteboardPracticeSelectionNotice";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { WhiteboardConversationSurface } from "./whiteboard/WhiteboardConversationSurface";
import {
  WHITEBOARD_INLINE_FRAME_CLASS,
  WhiteboardPhotoFrame,
} from "./whiteboard/WhiteboardPhotoFrame";
import { WhiteboardRecapGallery } from "./whiteboard/WhiteboardRecapGallery";

interface TranscriptionViewProps {
  onClose?: () => void;
  connectionState?: AgentConnectionState;
  connectionError?: string | null;
  retryConnection?: () => void;
  // Audio track error handling
  audioTrackError?: string | null;
  audioRetryCount?: number;
  retryAudioTrack?: () => void;
  clearAudioErrors?: () => void;
  // Session information for frozen header
  sessionDuration?: number; // in seconds
  selectedLanguageCode?: string;
  selectedLanguageLevel?: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
  isActiveSession?: boolean;
  // Opt in when the owning surface has no separate terminal recovery UI.
  showInactiveSessionRecovery?: boolean;
  recoverySurface?: VoiceDemoRecoverySurface;
  // Microphone control
  isMuted?: boolean;
  onToggleMute?: () => void;
  setMicrophoneMuted?: (muted: boolean) => void | Promise<void>;
  enableVoiceNotes?: boolean;
  disableAvatar?: boolean;
  onConversationActivity?: () => void;
  onOnboardingConversationMilestones?: (milestones: OnboardingConversationMilestones) => void;
  // Zen mode for meditation context
  isZenMode?: boolean;
  // Show speaking-session empty state copy even when session is not active
  showReadyStateWhenInactive?: boolean;
  // School page variant: hide advanced learning UI (language level badge, flashcards, hints)
  schoolMode?: boolean;
  // Display name for assistant/tutor in header and message labels.
  assistantDisplayName?: string;
  showHeaderWhenInactive?: boolean;
  renderRoomAudio?: boolean;
  tutorWhiteboardEnabled?: boolean;
  sessionMode?: SessionMode;
  learningMode?: "guided" | "conversation" | "journey";
  onLearningModeChange?: (mode: "conversation") => void;
  defaultTranslationLanguageCode?: string;
  instructionLanguageCode?: string;
  translationLanguageCode?: string;
  onTranslationLanguageChange?: (code: string) => void;
  translationsEnabled?: boolean;
  onTranslationsEnabledChange?: (enabled: boolean) => void;
  continuationConversation?: Conversation | null;
  previousTutorMessages?: string[];
  workspaceMode?: "standard" | "maths";
  whiteboardPortalId?: string;
  sessionControlsPortalId?: string;
  showSessionHeader?: boolean;
}

const MOBILE_WHITEBOARD_QUERY = "(max-width: 639px)";

function useMobileWhiteboardViewport() {
  const [isMobile, setIsMobile] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      setIsMobile(window.innerWidth < 640);
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_WHITEBOARD_QUERY);
    const update = () => setIsMobile(mediaQuery.matches);

    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return isMobile;
}

function getBaseLanguageCode(code: string | undefined): string {
  return String(code || "")
    .trim()
    .toLowerCase()
    .split("-")[0];
}

function TranscriptWidgetSkeleton() {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.035] p-4">
      <div className="mb-3 h-5 w-40 animate-pulse rounded bg-white/10" />
      <div className="space-y-2">
        <div className="h-4 w-full animate-pulse rounded bg-white/[0.08]" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-white/[0.08]" />
        <div className="h-10 w-32 animate-pulse rounded-md bg-orange-300/10" />
      </div>
    </div>
  );
}

function WhiteboardWidgetSkeleton() {
  return (
    <WhiteboardPhotoFrame
      className={`${WHITEBOARD_INLINE_FRAME_CLASS} my-5`}
      role="status"
      aria-label="Loading exercise whiteboard"
    >
      <div className="whiteboard-card--photo whiteboard-writing-surface relative w-full space-y-5 pr-12 pt-2 text-[#162033] sm:pr-14">
        <p className="text-lg font-semibold">Preparing your exercise...</p>
        <div className="h-3 w-5/6 rounded-full bg-slate-700/15" />
        <div className="h-3 w-2/3 rounded-full bg-slate-700/15" />
      </div>
    </WhiteboardPhotoFrame>
  );
}

const InteractiveWidget = dynamic(
  () => import("./InteractiveWidget").then((module) => module.InteractiveWidget),
  {
    loading: () => <WhiteboardWidgetSkeleton />,
    ssr: false,
  }
);

const BranchingCanvas = dynamic(
  () => import("./widgets/BranchingCanvas").then((module) => module.BranchingCanvas),
  {
    loading: () => <TranscriptWidgetSkeleton />,
    ssr: false,
  }
);

const FlowCanvasWidget = dynamic(
  () => import("./widgets/FlowCanvasWidget").then((module) => module.FlowCanvasWidget),
  {
    loading: () => <TranscriptWidgetSkeleton />,
    ssr: false,
  }
);

const SentenceMapWidget = dynamic(
  () => import("./widgets/SentenceMapWidget").then((module) => module.SentenceMapWidget),
  {
    loading: () => <TranscriptWidgetSkeleton />,
    ssr: false,
  }
);

interface MessageBubbleActionsProps {
  whiteboardSelectionId?: number;
  isLoading: boolean;
  isCopied: boolean;
  isZenMode?: boolean;
  onOpenSentenceBridge: () => void;
  onGenerateExercise: () => void;
  onCopy: () => void;
  onReplay?: () => void;
  replayState?: "idle" | "preparing" | "playing";
  replayDisabled?: boolean;
  replaySource?: "exact" | "resynth" | null;
  replayWholeResponse?: boolean;
}

function resolveTranscriptionFinality(segment: {
  isFinal?: boolean;
  final?: boolean;
}): boolean | undefined {
  if (typeof segment.isFinal === "boolean") return segment.isFinal;
  if (typeof segment.final === "boolean") return segment.final;
  return undefined;
}

function shouldShowAssistantMessageActions(
  role: "assistant" | "user",
  content: string | undefined | null,
  options?: { isLastMessage?: boolean; agentState?: string; isFinal?: boolean }
): boolean {
  if (role !== "assistant") return false;
  if (!content?.trim()) return false;

  const isFinal = options?.isFinal;

  // Hide on interim/streaming transcript chunks.
  if (isFinal === false) return false;

  if (options?.isLastMessage) {
    // The live tail must be explicitly marked final before actions appear.
    if (isFinal !== true) return false;

    const state = options.agentState;
    if (state && ["thinking", "speaking", "connecting"].includes(state)) return false;
  }

  return true;
}

function shouldShowUserMessageActions(
  role: "assistant" | "user",
  content: string | undefined | null
): boolean {
  return role === "user" && Boolean(content?.trim());
}

function MessageBubbleActions({
  whiteboardSelectionId,
  isLoading,
  isCopied,
  isZenMode,
  onOpenSentenceBridge,
  onGenerateExercise,
  onCopy,
  onReplay,
  replayState = "idle",
  replayDisabled = false,
  replaySource,
  replayWholeResponse = false,
}: MessageBubbleActionsProps) {
  const baseButtonClass =
    "inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors disabled:cursor-not-allowed disabled:opacity-60";
  const secondaryButtonClass = isZenMode
    ? "border-white/10 bg-white/[0.05] text-white/45 hover:border-white/20 hover:bg-white/[0.09] hover:text-white/80"
    : "border-white/10 bg-white/[0.04] text-white/45 hover:border-white/20 hover:bg-white/[0.07] hover:text-white/75";
  const bridgeButtonClass = isZenMode
    ? "border-cyan-400/20 bg-cyan-400/10 text-cyan-300/80 hover:border-cyan-400/35 hover:bg-cyan-400/15 hover:text-cyan-200"
    : "border-[#22D3EE]/30 bg-[#22D3EE]/10 text-[#22D3EE] hover:border-[#22D3EE]/50 hover:bg-[#22D3EE]/20 hover:text-[#67E8F9] shadow-[0_0_12px_rgba(34,211,238,0.12)]";
  return (
    <>
      {onReplay ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onReplay();
          }}
          disabled={replayDisabled || replayState === "preparing"}
          className={`${baseButtonClass} ${secondaryButtonClass}`}
          title={
            replayState === "preparing"
              ? "Preparing tutor replay"
              : replayState === "playing"
                ? replaySource === "resynth"
                  ? "Stop re-created tutor audio"
                  : "Stop original tutor audio"
                : replayWholeResponse
                  ? "Replay full tutor response"
                  : "Replay tutor message"
          }
          aria-label={
            replayState === "preparing"
              ? "Preparing tutor replay"
              : replayState === "playing"
                ? "Stop tutor replay"
                : replayWholeResponse
                  ? "Replay full tutor response"
                  : "Replay tutor message"
          }
        >
          {replayState === "preparing" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : replayState === "playing" ? (
            <Square className="h-3.5 w-3.5 fill-current" />
          ) : (
            <Volume2 className="h-4 w-4" />
          )}
        </button>
      ) : null}

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onOpenSentenceBridge();
        }}
        disabled={isLoading}
        data-transcript-action="bridge"
        className={`${baseButtonClass} ${bridgeButtonClass}`}
        title={isLoading ? "Building SentenceBridge" : "SentenceBridge"}
        aria-label={isLoading ? "Building SentenceBridge" : "Open SentenceBridge"}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <BookOpen className="h-4 w-4" />
        )}
      </button>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onGenerateExercise();
        }}
        className={`${baseButtonClass} ${secondaryButtonClass}`}
        title="Practice this"
        aria-label="Practice this"
      >
        <Presentation className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onCopy();
        }}
        className={`${baseButtonClass} ${secondaryButtonClass}`}
        title={isCopied ? "Copied" : "Copy message"}
        aria-label={isCopied ? "Message copied" : "Copy message"}
      >
        {isCopied ? <Check className="h-4 w-4 text-[#22C55E]" /> : <Copy className="h-4 w-4" />}
      </button>
      <WhiteboardPracticeSelectionNotice selectionId={whiteboardSelectionId} />
    </>
  );
}

function UserMessageActionsMenu({
  whiteboardSelectionId,
  isLoading,
  isCopied,
  isZenMode,
  onOpenSentenceBridge,
  onGenerateExercise,
  onCopy,
}: MessageBubbleActionsProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const menuSurfaceClass = isZenMode
    ? "border-white/10 bg-[#111827]/95 text-white/80"
    : "border-white/10 bg-[#1b1c22] text-white/80";
  const triggerClass = isZenMode
    ? "text-white/55 hover:bg-white/[0.1] hover:text-white/90"
    : "text-white/45 hover:bg-white/[0.07] hover:text-white/75";
  const itemClass =
    "flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs font-semibold transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-60";

  const runAction = (action: () => void) => {
    setIsOpen(false);
    action();
  };

  return (
    <div
      className="relative inline-flex flex-wrap items-center gap-2"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`inline-flex h-5 w-5 items-center justify-center rounded transition-colors ${triggerClass}`}
        aria-label="Message actions"
        aria-expanded={isOpen}
        title="Message actions"
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>

      {isOpen ? (
        <div
          className={`absolute right-0 top-full z-30 mt-1.5 min-w-[11rem] rounded-md border p-1.5 shadow-2xl shadow-black/30 ${menuSurfaceClass}`}
        >
          <button
            type="button"
            onClick={() => runAction(onOpenSentenceBridge)}
            disabled={isLoading}
            className={itemClass}
            aria-label={isLoading ? "Building SentenceBridge" : "Open SentenceBridge"}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-[#22D3EE]" />
            ) : (
              <BookOpen className="h-4 w-4 text-[#22D3EE]" />
            )}
            <span>SentenceBridge</span>
          </button>
          <button
            type="button"
            onClick={() => runAction(onGenerateExercise)}
            className={itemClass}
            aria-label="Practice this"
          >
            <Presentation className="h-4 w-4 text-orange-300" />
            <span>Practice this</span>
          </button>
          <button
            type="button"
            onClick={() => runAction(onCopy)}
            className={itemClass}
            aria-label={isCopied ? "Message copied" : "Copy message"}
          >
            {isCopied ? (
              <Check className="h-4 w-4 text-[#22C55E]" />
            ) : (
              <Copy className="h-4 w-4 text-[#AEAEB2]" />
            )}
            <span>{isCopied ? "Copied" : "Copy"}</span>
          </button>
        </div>
      ) : null}
      <WhiteboardPracticeSelectionNotice selectionId={whiteboardSelectionId} />
    </div>
  );
}

export default function TranscriptionView({
  onClose,
  connectionState = "connected",
  connectionError = null,
  retryConnection,
  audioTrackError = null,
  audioRetryCount = 0,
  retryAudioTrack,
  clearAudioErrors,
  sessionDuration = 0,
  selectedLanguageCode,
  selectedLanguageLevel = "beginner",
  isActiveSession = false,
  showInactiveSessionRecovery = false,
  recoverySurface,
  isMuted = false,
  onToggleMute,
  setMicrophoneMuted,
  enableVoiceNotes = false,
  disableAvatar = false,
  onConversationActivity,
  onOnboardingConversationMilestones,
  isZenMode = false,
  showReadyStateWhenInactive = true,
  schoolMode = false,
  assistantDisplayName,
  showHeaderWhenInactive = true,
  renderRoomAudio = true,
  tutorWhiteboardEnabled = false,
  sessionMode,
  learningMode,
  onLearningModeChange,
  defaultTranslationLanguageCode,
  instructionLanguageCode,
  translationLanguageCode: controlledTranslationLanguageCode,
  onTranslationLanguageChange,
  translationsEnabled: controlledTranslationsEnabled,
  onTranslationsEnabledChange,
  continuationConversation = null,
  previousTutorMessages = [],
  workspaceMode = "standard",
  whiteboardPortalId,
  sessionControlsPortalId,
  showSessionHeader = true,
}: TranscriptionViewProps) {
  const isMathsWorkspace = workspaceMode === "maths";
  const logger = React.useMemo(() => createLogger("auto-summary"), []);
  const combinedTranscriptions = useCombinedTranscriptions();
  const onboardingConversationMilestones = React.useMemo(
    () => getOnboardingConversationMilestones(combinedTranscriptions),
    [combinedTranscriptions]
  );
  React.useEffect(() => {
    onOnboardingConversationMilestones?.(onboardingConversationMilestones);
  }, [onOnboardingConversationMilestones, onboardingConversationMilestones]);
  const showDevUserTranscripts = shouldShowDevUserTranscripts();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const lastMessageRef = React.useRef<HTMLDivElement>(null);
  const room = useRoomContext();
  const { agent, state: rawAgentState, agentAttributes } = useVoiceAssistant();
  const agentState = extendedInteractionState(rawAgentState, agentAttributes); // Get agent state to know when speaking is done
  const recoveryKind = resolveVoiceSessionRecoveryKind({
    audioTrackError,
    connectionState,
    isActiveSession,
    showInactiveSessionRecovery,
  });
  const trackedRecovery = useVoiceDemoRecoveryTelemetry({
    audioRetryCount,
    audioTrackError,
    connectionError,
    connectionState,
    enabled: showInactiveSessionRecovery,
    isConnected: isActiveSession,
    onClose,
    recoveryKind,
    retryAudioTrack,
    retryConnection,
    surface: recoverySurface,
  });
  const { uploadRecentSpeakingMemories } = useSpeakingMemoryCapture(
    isActiveSession && !isMathsWorkspace
  );
  const voiceNote = useVoiceNoteTurn({
    room,
    agentIdentity: agent?.identity,
    enabled: enableVoiceNotes && !isZenMode && !schoolMode,
    isConnected: isActiveSession,
    isMuted,
    setMuted: setMicrophoneMuted,
    maxDurationSeconds: 60,
  });
  const showVoiceNotes = enableVoiceNotes && (voiceNote.isSupported || voiceNote.state !== "idle");
  const ttsRuntimeControls = useTtsRuntimeControls({
    room,
    agentIdentity: agent?.identity,
    enabled: isActiveSession,
  });
  const tutorReplay = useTutorReplay({
    room,
    agentIdentity: agent?.identity,
    enabled: isActiveSession,
    isMuted,
    setMuted: setMicrophoneMuted,
  });

  // Widget state (exercise widget + A2UI Flow Canvas)
  const {
    showWidget,
    isWidgetLoading,
    widgetError,
    openWidget,
    closeWidget,
    // A2UI Flow Canvas
    flowCanvasMessages,
    isFlowCanvasLoading,
    flowCanvasError,
    showFlowCanvas,
    generateFlowCanvas,
    closeFlowCanvas,
    sentenceMapMessages,
    isSentenceMapLoading,
    sentenceMapError,
    showSentenceMap,
    sentenceMapAnchorKey,
    sentenceMapRequestId,
    generateSentenceMap,
    closeSentenceMap,
    retrySentenceMap,
  } = useInteractiveWidget();

  const sentenceMapBoard =
    !schoolMode && showSentenceMap ? (
      <SentenceMapWidget
        key={sentenceMapRequestId}
        messages={sentenceMapMessages}
        isLoading={isSentenceMapLoading}
        error={sentenceMapError}
        onClose={closeSentenceMap}
        onRetry={retrySentenceMap}
      />
    ) : null;

  const [widgetPrompt, setWidgetPrompt] = React.useState("");

  // Tutor-driven whiteboard (agent pushes exercises over the data channel)
  const {
    spec: whiteboardSpec,
    renderState: whiteboardRenderState,
    minimizedByAgent: whiteboardMinimizedByAgent,
    tutorRevision: whiteboardTutorRevision,
    boardHistory: whiteboardBoardHistory,
    sendCardEvent: sendWhiteboardEvent,
    reportPresentation: reportWhiteboardPresentation,
    observeUserSpeech: observeWhiteboardUserSpeech,
    restore: restoreWhiteboard,
  } = useWhiteboard(room, { countsAsLanguagePractice: !isMathsWorkspace });
  const [isWhiteboardFocused, setIsWhiteboardFocused] = React.useState(false);
  const isMobileWhiteboardViewport = useMobileWhiteboardViewport();
  const [whiteboardPortalTarget, setWhiteboardPortalTarget] = React.useState<HTMLElement | null>(
    null
  );
  React.useEffect(() => {
    setWhiteboardPortalTarget(
      whiteboardPortalId ? document.getElementById(whiteboardPortalId) : null
    );
  }, [whiteboardPortalId]);
  const [sessionControlsPortalTarget, setSessionControlsPortalTarget] =
    React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    setSessionControlsPortalTarget(
      sessionControlsPortalId ? document.getElementById(sessionControlsPortalId) : null
    );
  }, [sessionControlsPortalId]);

  // Latest board results, readable from stable callbacks (session artifacts).
  const whiteboardResultsRef = React.useRef<WhiteboardBoardResult[]>([]);
  React.useEffect(() => {
    whiteboardResultsRef.current = whiteboardBoardHistory.map((board) => board.result);
  }, [whiteboardBoardHistory]);

  const [chatInput, setChatInput] = React.useState("");
  const { typedMessages, sendTypedMessage } = useTypedTranscriptMessages(
    room,
    combinedTranscriptions
  );
  const [isSummarizing, setIsSummarizing] = React.useState(false);

  // TTS speak function for Flow Canvas hints
  const handleSpeakText = React.useCallback(
    async (text: string) => {
      // Use browser speech synthesis as fallback
      if ("speechSynthesis" in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        // Try to find a voice for the target language
        const voices = speechSynthesis.getVoices();
        const langCode = selectedLanguageCode?.toLowerCase() || "en";
        const matchingVoice = voices.find((v) => v.lang.toLowerCase().startsWith(langCode));
        if (matchingVoice) {
          utterance.voice = matchingVoice;
        }
        speechSynthesis.speak(utterance);
      }
    },
    [selectedLanguageCode]
  );

  // Handle selecting a path from Flow Canvas
  const handleSelectPath = React.useCallback(
    async (text: string) => {
      if (!room) return;
      try {
        // Send the selected phrase to the conversation
        await sendTypedMessage(text);
      } catch (err) {
        logger.error("Failed to send hint text to LiveKit", {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [room, sendTypedMessage]
  );

  // Enhanced scroll state management
  const [userHasScrolledUp, setUserHasScrolledUp] = React.useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = React.useState(false);
  const lastScrollPositionRef = React.useRef<number>(0);
  const isAutoScrollingRef = React.useRef<boolean>(false);
  const scrollDebounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const autoScrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const persistDebounceRef = React.useRef<NodeJS.Timeout | null>(null);
  const prevIsActiveSessionRef = React.useRef<boolean>(false);
  const hasAutoSavedSummaryRef = React.useRef<boolean>(false);
  const pendingSummaryDataRef = React.useRef<{
    messages: Array<{ role: string; content: string; timestamp: number }>;
    conversationId: string;
    languageName: string;
    languageLevel: string;
    accessToken: string;
  } | null>(null);

  // Mobile device detection
  const isMobileDevice = React.useMemo(() => {
    if (typeof window !== "undefined") {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    }
    return false;
  }, []);

  // Conversation persistence only (no sidebar here)
  const {
    currentConversation,
    currentConversationId,
    setCurrentConversationId,
    createNewConversation,
    adoptConversation,
    updateConversation,
    loading,
  } = useConversationHistory(onConversationActivity);

  React.useEffect(() => {
    closeSentenceMap();
  }, [currentConversationId, isActiveSession, closeSentenceMap]);

  React.useEffect(() => {
    if (continuationConversation) adoptConversation(continuationConversation);
  }, [adoptConversation, continuationConversation]);

  // Flashcard tooltip state
  const [selectedSentence, setSelectedSentence] = React.useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = React.useState<WordActionAnchor>({
    x: 0,
    y: 0,
  });

  // Word action bar state (mini popup before flashcard)
  const [actionBarWord, setActionBarWord] = React.useState<string | null>(null);
  const [actionBarPosition, setActionBarPosition] = React.useState<WordActionAnchor>({
    x: 0,
    y: 0,
  });
  const [wordTapHintDismissed, setWordTapHintDismissed] = React.useState(false);

  // Auth and flashcard management
  const { user } = useAuth();
  const { isFlashcardSaved, isFlashcardFavorited, saveFlashcard, toggleFavorite } = useFlashcards(
    user?.id
  );

  // Session Summary State
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [lastSummary, setLastSummary] = useState<string | null>(null);
  const [hasAvailableSummary, setHasAvailableSummary] = useState(false);
  const [copiedMessageKey, setCopiedMessageKey] = React.useState<string | null>(null);
  const copyResetTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Track if we've automatically shown the summary this session
  const hasShownAutoSummaryRef = React.useRef(false);

  const handleCopyTranscriptMessage = React.useCallback(
    async (content: string, messageKey: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(content);
        } else {
          const textArea = document.createElement("textarea");
          textArea.value = content;
          textArea.style.position = "fixed";
          textArea.style.left = "-9999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
        }

        setCopiedMessageKey(messageKey);
        if (copyResetTimeoutRef.current) {
          clearTimeout(copyResetTimeoutRef.current);
        }
        copyResetTimeoutRef.current = setTimeout(() => setCopiedMessageKey(null), 1400);
      } catch (err) {
        logger.error("Failed to copy transcript message", {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [logger]
  );

  React.useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  // Fetch summary logic extracted for reuse
  const fetchSessionSummary = React.useCallback(async () => {
    if (isMathsWorkspace) return null;
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const headers: HeadersInit = {};
      if (session?.access_token) {
        headers["Authorization"] = `Bearer ${session.access_token}`;
      }

      // Find the language name from the code
      const languageName = Object.entries(languages).find(
        ([_, code]) => code === selectedLanguageCode
      )?.[0];

      const url = new URL("/api/progress/session-summary", window.location.origin);
      if (languageName) {
        url.searchParams.set("language", languageName);
      }

      const response = await fetch(url.toString(), {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.summaries && data.summaries.length > 0) {
          return data.summaries[0].summary;
        } else if (data && Array.isArray(data) && data.length > 0) {
          return data[0].summary;
        }
      }
    } catch (error) {
      console.error("Error fetching session summary:", error);
    }
    return null;
  }, [isMathsWorkspace, selectedLanguageCode]);

  const handleViewSummary = async () => {
    const summary = await fetchSessionSummary();
    if (summary) {
      setLastSummary(summary);
      setIsSummaryModalOpen(true);
    } else {
      console.warn("No session summary found.");
    }
  };

  // Check for summary availability on mount
  React.useEffect(() => {
    // Reset state on mount or language change
    setHasAvailableSummary(false);

    const checkSummary = async () => {
      const summary = await fetchSessionSummary();
      if (summary) {
        setLastSummary(summary);
        setHasAvailableSummary(true);
      }
    };

    if (!isMathsWorkspace) checkSummary();
  }, [fetchSessionSummary, isMathsWorkspace]);

  // Derive language name from code for conversation metadata
  const languageNameMemo = React.useMemo(() => {
    if (!selectedLanguageCode) return "";
    return (
      Object.entries(languages).find(([, c]) => c === selectedLanguageCode)?.[0] ||
      selectedLanguageCode
    );
  }, [selectedLanguageCode]);

  const resolvedLearningMode =
    learningMode ?? (sessionMode === "conversation" ? "conversation" : undefined);
  const translationFeatureAvailable =
    process.env.NEXT_PUBLIC_CONVERSATION_TRANSLATIONS_ENABLED !== "false" &&
    sessionMode === "conversation" &&
    resolvedLearningMode === "conversation" &&
    !isZenMode &&
    !schoolMode;
  const translationLanguageOptions = React.useMemo(() => {
    const sourceBaseCode = getBaseLanguageCode(selectedLanguageCode);
    return Object.entries(languages)
      .map(([name, code]) => ({ name, code }))
      .filter((option) => getBaseLanguageCode(option.code) !== sourceBaseCode)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [selectedLanguageCode]);
  const {
    translationsEnabled,
    setTranslationsEnabled,
    translationLanguageCode: effectiveTranslationLanguageCode,
    setTranslationLanguageCode,
  } = useTranscriptTranslationSettings({
    isActiveSession,
    targetLanguageCode: selectedLanguageCode || "",
    defaultLanguageCode: defaultTranslationLanguageCode,
    languageCode: controlledTranslationLanguageCode,
    onLanguageChange: onTranslationLanguageChange,
    enabled: controlledTranslationsEnabled,
    onEnabledChange: onTranslationsEnabledChange,
  });

  const handleLearningModeChanged = React.useCallback(
    (mode: "conversation") => {
      setTranslationsEnabled(true);
      onLearningModeChange?.(mode);
    },
    [onLearningModeChange, setTranslationsEnabled]
  );
  const learningModeTransition = useLearningModeTransition({
    room,
    agentIdentity: agent?.identity,
    enabled:
      isActiveSession &&
      learningMode === "guided" &&
      sessionMode === "conversation" &&
      !isZenMode &&
      !schoolMode,
    targetLanguage: languageNameMemo || selectedLanguageCode || "the target language",
    onChanged: handleLearningModeChanged,
  });

  const effectiveTranslationLanguage =
    translationLanguageOptions.find((option) => option.code === effectiveTranslationLanguageCode)
      ?.name || "Translation";
  const instructionLanguageMemo = effectiveTranslationLanguage;
  const sentenceMapInstructionLanguage = getTranslationLanguageName(
    resolveSentenceMapLanguageCode({
      targetLanguageCode: selectedLanguageCode || "",
      instructionLanguageCode: instructionLanguageCode ?? defaultTranslationLanguageCode,
      translationLanguageCode: effectiveTranslationLanguageCode,
      learningMode: resolvedLearningMode,
    })
  );

  React.useEffect(() => {
    closeSentenceMap();
  }, [selectedLanguageCode, sentenceMapInstructionLanguage, closeSentenceMap]);

  // Initialize conversation when none selected
  React.useEffect(() => {
    if (loading) return; // wait for source (backend/local) to be determined
    if (!currentConversationId && !continuationConversation && languageNameMemo) {
      (async () => {
        const newId = await createNewConversation(
          languageNameMemo,
          selectedLanguageLevel,
          "speaking"
        );
        setCurrentConversationId(newId);
      })();
    }
  }, [
    loading,
    currentConversationId,
    createNewConversation,
    setCurrentConversationId,
    languageNameMemo,
    selectedLanguageLevel,
    continuationConversation,
  ]);

  useTranscriptPracticeActivity(combinedTranscriptions, typedMessages, !isMathsWorkspace);

  // Convert LiveKit transcriptions into conversation messages and sync to backend
  const allMessages = React.useMemo(() => {
    const visibleVoiceMessages = combinedTranscriptions
      .filter(
        (seg: any) =>
          seg && (seg.role === "assistant" || (showDevUserTranscripts && seg.role === "user"))
      )
      .filter((seg: any) => typeof seg.text === "string" && seg.text.trim().length > 0);

    const displayMessages = [
      ...visibleVoiceMessages,
      ...typedMessages.filter((item) => !item.pending),
    ]
      .filter((seg: any) => typeof seg.text === "string" && seg.text.trim().length > 0)
      .sort((a: any, b: any) => (a.firstReceivedTime || 0) - (b.firstReceivedTime || 0));

    return displayMessages;
  }, [combinedTranscriptions, showDevUserTranscripts, typedMessages]);

  const displayMessages = React.useMemo(
    () =>
      splitTypedTranscriptDisplay(
        combinedTranscriptions.filter((seg) => seg.role === "assistant" || showDevUserTranscripts),
        typedMessages
      ),
    [combinedTranscriptions, showDevUserTranscripts, typedMessages]
  );

  const [tutorReplayMessageIds, setTutorReplayMessageIds] = React.useState<
    ReadonlyMap<string, string>
  >(() => new Map());
  React.useEffect(() => {
    let cancelled = false;
    void buildTutorReplayMessageIds(allMessages).then((identities) => {
      if (!cancelled) setTutorReplayMessageIds(identities);
    });
    return () => {
      cancelled = true;
    };
  }, [allMessages]);

  const hasFinalTutorTurn = React.useMemo(
    () => hasFinalizedTutorTurn(allMessages as TutorTranscriptSegment[]),
    [allMessages]
  );

  const translationsActive =
    translationFeatureAvailable &&
    isActiveSession &&
    translationsEnabled &&
    Boolean(selectedLanguageCode) &&
    Boolean(effectiveTranslationLanguageCode);
  const { getTranslation: getTutorTurnTranslation, retryTranslation } = useTutorTurnTranslations({
    segments: displayMessages as TutorTranscriptSegment[],
    enabled: translationsActive,
    sourceLanguageCode: selectedLanguageCode || "",
    translationLanguageCode: effectiveTranslationLanguageCode,
    translationLanguage: effectiveTranslationLanguage,
  });

  const firstAssistantMessageIndex = React.useMemo(
    () => displayMessages.findIndex((message) => message.role === "assistant"),
    [displayMessages]
  );

  const sessionArtifactMessages = React.useMemo(
    () =>
      buildSessionArtifactMessages(
        combinedTranscriptions,
        typedMessages.filter((item) => !item.pending)
      ),
    [combinedTranscriptions, typedMessages]
  );

  const latestFinalUserSpeech = React.useMemo(() => {
    const userMessages = combinedTranscriptions
      .filter((seg: any) => seg?.role === "user")
      .filter((seg: any) => typeof seg.text === "string" && seg.text.trim().length > 0)
      .filter((seg: any) => !String(seg.id || "").startsWith("active_user_"))
      .filter((seg: any) => resolveTranscriptionFinality(seg) !== false)
      .sort((a: any, b: any) => (a.firstReceivedTime || 0) - (b.firstReceivedTime || 0));
    return userMessages[userMessages.length - 1] ?? null;
  }, [combinedTranscriptions]);
  const observedWhiteboardSpeechRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!latestFinalUserSpeech) return;
    const speechId = getWhiteboardSpeechObservationId(latestFinalUserSpeech);
    if (observedWhiteboardSpeechRef.current === speechId) return;
    observedWhiteboardSpeechRef.current = speechId;
    observeWhiteboardUserSpeech(String(latestFinalUserSpeech.text), speechId);
  }, [latestFinalUserSpeech, observeWhiteboardUserSpeech]);

  const handleGenerateSentenceMapFromMessage = React.useCallback(
    (
      messages: Array<{ role: string; text?: string; content?: string }>,
      messageIndex: number,
      anchorKey: string
    ) => {
      const selectedMessage = messages[messageIndex];
      const focusedMessage = selectedMessage
        ? {
            role: selectedMessage.role,
            content: String(selectedMessage.text ?? selectedMessage.content ?? "").trim(),
          }
        : undefined;
      const context = messages
        .slice(Math.max(0, messageIndex - 2), messageIndex + 1)
        .map((message) => ({
          role: message.role,
          content: String(message.text ?? message.content ?? "").trim(),
        }))
        .filter((message) => message.content.length > 0);

      generateSentenceMap(
        context,
        languageNameMemo || "English",
        sentenceMapInstructionLanguage,
        selectedLanguageLevel,
        focusedMessage?.content ? focusedMessage : undefined,
        anchorKey
      );
    },
    [generateSentenceMap, sentenceMapInstructionLanguage, languageNameMemo, selectedLanguageLevel]
  );

  const handleGenerateExerciseFromMessage = React.useCallback(
    (messages: Array<{ role: string; text?: string; content?: string }>, messageIndex: number) => {
      const recentContext = messages
        .slice(Math.max(0, messageIndex - 2), messageIndex + 2)
        .map((message) => {
          const content = String(message.text ?? message.content ?? "").trim();
          return content ? `${message.role}: ${content}` : "";
        })
        .filter(Boolean)
        .join("\n");

      const prompt = `Create an interactive exercise for a ${selectedLanguageLevel} ${selectedLanguageCode || languageNameMemo || "English"} learner based on this transcript message and nearby context:\n${recentContext}`;
      setWidgetPrompt(prompt);
      openWidget();
    },
    [languageNameMemo, openWidget, selectedLanguageCode, selectedLanguageLevel]
  );

  const handleGenerateConversationExercise = React.useCallback(() => {
    const recentContext = allMessages
      .slice(-5)
      .map((message: any) => `${message.role}: ${message.text}`)
      .join("\n");
    const prompt = `Create an interactive exercise for a ${selectedLanguageLevel} ${selectedLanguageCode || "English"} learner based on this conversation:\n${recentContext}`;
    setWidgetPrompt(prompt);
    openWidget();
  }, [allMessages, openWidget, selectedLanguageCode, selectedLanguageLevel]);

  // Branching Canvas - Concept Tree visualization
  const branchingCanvas = useBranchingCanvas({
    messages: allMessages.map((m: any) => ({
      role: m.role,
      content: m.text,
      timestamp: m.firstReceivedTime,
    })),
    targetLanguage: languageNameMemo || "English",
    languageLevel: selectedLanguageLevel,
  });

  React.useEffect(() => {
    if (!currentConversationId) return;

    const finished = allMessages;

    const prev: ConversationMessage[] = currentConversation?.messages || [];
    const next: ConversationMessage[] = finished.map((seg: any, idx: number) => {
      const mapped: ConversationMessage = {
        role: seg.role,
        content: seg.text,
        timestamp: typeof seg.firstReceivedTime === "number" ? seg.firstReceivedTime : Date.now(),
      };
      // Preserve timestamp if unchanged for stable history ordering
      const p = prev[idx];
      if (p && p.role === mapped.role && p.content === mapped.content) return p;
      return mapped;
    });

    // Avoid redundant updates if identical
    const same =
      prev.length === next.length &&
      next.every((m, i) => prev[i] && prev[i].role === m.role && prev[i].content === m.content);
    if (same) return;

    // Debounced persist to reduce API churn
    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
    }
    persistDebounceRef.current = setTimeout(() => {
      updateConversation(currentConversationId, next);
    }, 500);
  }, [allMessages, currentConversationId, currentConversation?.messages, updateConversation]);

  // Helper to send session artifacts (summary + diary) used by normal flow and beforeunload
  const sendSessionSummary = React.useCallback(
    async (
      messages: Array<{ role: string; content: string; timestamp: number }>,
      conversationId: string,
      languageName: string,
      languageLevel: string,
      accessToken: string,
      useBeacon: boolean = false
    ): Promise<boolean> => {
      if (isMathsWorkspace) return true;
      logger.info("Session artifact save started", {
        useBeacon,
        messageCount: messages.length,
        conversationId,
        languageName,
        languageLevel,
        hasAccessToken: Boolean(accessToken),
      });

      const payload = {
        messages,
        conversationId,
        languageName,
        languageLevel,
        sessionMode: isZenMode ? "zen" : "conversation",
        whiteboardResults: whiteboardResultsRef.current,
        accessToken, // Include token in body for beacon (can't use headers)
      };

      if (useBeacon && navigator.sendBeacon) {
        // sendBeacon for tab close - survives page unload
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        const summarySent = navigator.sendBeacon("/api/progress/session-summary", blob);
        const diarySent = navigator.sendBeacon("/api/progress/tutor-diary", blob);
        logger.info("Session artifact sendBeacon attempted", {
          summarySent,
          diarySent,
          messageCount: messages.length,
        });
        return summarySent && diarySent;
      }

      // Normal fetch for regular session end
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        };

        logger.info("Speaking memory upload started", {
          conversationId,
          messageCount: sessionArtifactMessages.length,
        });

        const speakingMemoryResult = await uploadRecentSpeakingMemories({
          conversationId,
          languageName,
          languageLevel,
          messages: sessionArtifactMessages.slice(-80) as any,
          accessToken,
        });

        logger.info("Speaking memory upload finished", {
          uploaded: speakingMemoryResult.uploaded,
          conversationId,
        });

        logger.info("Summary and diary POST started", {
          conversationId,
          messageCount: messages.length,
        });

        const [summaryRes, diaryRes] = await Promise.all([
          fetch("/api/progress/session-summary", {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
          }),
          fetch("/api/progress/tutor-diary", {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
          }),
        ]);

        logger.info("Summary and diary POST finished", {
          summaryStatus: summaryRes.status,
          diaryStatus: diaryRes.status,
          conversationId,
        });

        if (summaryRes.ok) {
          const json = await summaryRes.json();
          logger.info("Session summary saved", {
            summaryId: json.id,
            messageCount: messages.length,
          });
          if (!diaryRes.ok) {
            logger.warn("Tutor diary save failed (non-fatal)", {
              status: diaryRes.status,
              messageCount: messages.length,
            });
          }
          return true;
        } else {
          logger.error("Failed to save session summary", {
            status: summaryRes.status,
            messageCount: messages.length,
          });
          return false;
        }
      } catch (err) {
        logger.error("Error saving session summary", {
          error: err instanceof Error ? err.message : String(err),
          messageCount: messages.length,
        });
        return false;
      }
    },
    [isMathsWorkspace, isZenMode, sessionArtifactMessages, uploadRecentSpeakingMemories]
  );

  // Keep pending summary data updated during active session (for beforeunload)
  React.useEffect(() => {
    if (
      !isMathsWorkspace &&
      isActiveSession &&
      allMessages.length >= 5 &&
      currentConversationId &&
      languageNameMemo
    ) {
      // Update pending data asynchronously
      (async () => {
        const { data } = await supabase.auth.getSession();
        const access = data?.session?.access_token;
        if (!access) return;

        pendingSummaryDataRef.current = {
          messages: sessionArtifactMessages.slice(-80).map((seg) => ({
            role: seg.role,
            content: seg.text,
            timestamp: seg.firstReceivedTime,
          })),
          conversationId: currentConversationId,
          languageName: languageNameMemo,
          languageLevel: selectedLanguageLevel,
          accessToken: access,
        };
      })();
    }
  }, [
    isActiveSession,
    allMessages,
    currentConversationId,
    languageNameMemo,
    selectedLanguageLevel,
    sessionArtifactMessages,
    isMathsWorkspace,
  ]);

  // Handle tab close with sendBeacon (survives page unload)
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      const pending = pendingSummaryDataRef.current;
      if (pending && isActiveSession && !hasAutoSavedSummaryRef.current) {
        logger.info("Beforeunload session artifact save triggered", {
          messageCount: pending.messages.length,
          conversationId: pending.conversationId,
        });
        void sendSessionSummary(
          pending.messages,
          pending.conversationId,
          pending.languageName,
          pending.languageLevel,
          pending.accessToken,
          true // useBeacon
        ).then((saved) => {
          hasAutoSavedSummaryRef.current = saved;
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isActiveSession, sendSessionSummary]);

  // More reliable: detect session end directly from LiveKit disconnect event
  React.useEffect(() => {
    const handleDisconnect = () => {
      const pending = pendingSummaryDataRef.current;
      logger.info("LiveKit disconnect handler entered", {
        hasPending: Boolean(pending),
        hasAutoSaved: hasAutoSavedSummaryRef.current,
      });

      // Only auto-save if we have pending data and haven't saved yet
      if (pending && !hasAutoSavedSummaryRef.current) {
        logger.info("Auto-saving session summary on disconnect", {
          messageCount: pending.messages.length,
        });

        void sendSessionSummary(
          pending.messages,
          pending.conversationId,
          pending.languageName,
          pending.languageLevel,
          pending.accessToken,
          false // use normal fetch
        ).then((saved) => {
          if (saved) {
            hasAutoSavedSummaryRef.current = true;
            pendingSummaryDataRef.current = null;
          } else {
            logger.warn("Auto-save on disconnect did not complete successfully");
          }
        });
      } else if (!pending) {
        pendingSummaryDataRef.current = null;
      }
    };

    // Listen for LiveKit disconnect events
    if (room) {
      logger.debug("Registering LiveKit disconnect handler");
      room.on("disconnected", handleDisconnect);
    }

    return () => {
      if (room) {
        room.off("disconnected", handleDisconnect);
      }
    };
  }, [room, sendSessionSummary]);

  React.useEffect(() => {
    const wasActive = prevIsActiveSessionRef.current;

    if (!wasActive && isActiveSession) {
      logger.info("Session became active; resetting auto-save state");
      hasAutoSavedSummaryRef.current = false;
    }

    if (wasActive && !isActiveSession) {
      const pending = pendingSummaryDataRef.current;
      logger.info("Session active state changed to inactive", {
        hasPending: Boolean(pending),
        hasAutoSaved: hasAutoSavedSummaryRef.current,
      });

      if (pending && !hasAutoSavedSummaryRef.current) {
        logger.info("Auto-saving session artifacts on active-state transition", {
          messageCount: pending.messages.length,
          conversationId: pending.conversationId,
        });

        void sendSessionSummary(
          pending.messages,
          pending.conversationId,
          pending.languageName,
          pending.languageLevel,
          pending.accessToken,
          false
        ).then((saved) => {
          if (saved) {
            hasAutoSavedSummaryRef.current = true;
            pendingSummaryDataRef.current = null;
          } else {
            logger.warn("Auto-save on inactive transition did not complete successfully");
          }
        });
      } else if (!pending) {
        logger.warn("Session became inactive without pending artifact data");
      }
    }

    prevIsActiveSessionRef.current = isActiveSession;
  }, [isActiveSession, sendSessionSummary]);

  // No sidebar handlers in this view

  // Enhanced scroll detection with debouncing
  React.useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const handleScroll = () => {
      // Ignore scroll events during auto-scrolling
      if (isAutoScrollingRef.current) {
        return;
      }

      const currentScrollTop = container.scrollTop;
      const maxScrollTop = container.scrollHeight - container.clientHeight;
      const scrollThreshold = 100; // Increased threshold for better stability

      // Clear existing debounce timeout
      if (scrollDebounceTimeoutRef.current) {
        clearTimeout(scrollDebounceTimeoutRef.current);
      }

      // Debounce scroll detection to avoid jittery behavior
      scrollDebounceTimeoutRef.current = setTimeout(() => {
        const isNearBottom = maxScrollTop - currentScrollTop < scrollThreshold;
        const hasScrolledUp = !isNearBottom && maxScrollTop > 0;

        // Only update state if there's a meaningful change
        const scrolledUpward = currentScrollTop < lastScrollPositionRef.current - 10;
        const scrolledDownward = currentScrollTop > lastScrollPositionRef.current + 10;

        if (hasScrolledUp && scrolledUpward) {
          // User intentionally scrolled up
          setUserHasScrolledUp(true);
          setShowScrollToBottom(true);
        } else if (isNearBottom && scrolledDownward) {
          // User scrolled back down to near bottom
          setUserHasScrolledUp(false);
          setShowScrollToBottom(false);
        }

        lastScrollPositionRef.current = currentScrollTop;
      }, 100); // Reduced debounce for better responsiveness
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollDebounceTimeoutRef.current) {
        clearTimeout(scrollDebounceTimeoutRef.current);
      }
    };
  }, []);

  // More conservative reset of scroll state when new messages arrive
  React.useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const maxScrollTop = container.scrollHeight - container.clientHeight;
    const currentScrollTop = container.scrollTop;

    // Only reset if user is very close to bottom (within 20px) AND
    // they haven't manually scrolled recently
    const isVeryCloseToBottom = maxScrollTop - currentScrollTop < 20;

    // Only reset if user is extremely close to bottom to avoid conflicts
    if (isVeryCloseToBottom && maxScrollTop > 0) {
      setUserHasScrolledUp(false);
      setShowScrollToBottom(false);
    }
  }, [allMessages]);

  // Smooth scroll functions
  const smoothScrollToBottom = React.useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    isAutoScrollingRef.current = true;

    // Cancel any existing animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startPosition = container.scrollTop;
    const targetPosition = container.scrollHeight - container.clientHeight;
    const distance = targetPosition - startPosition;
    const duration = 300; // ms
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out animation
      const easeOutProgress = 1 - Math.pow(1 - progress, 3);

      container.scrollTop = startPosition + distance * easeOutProgress;

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animateScroll);
      } else {
        isAutoScrollingRef.current = false;
        setUserHasScrolledUp(false);
        setShowScrollToBottom(false);
        lastScrollPositionRef.current = container.scrollTop;
      }
    };

    animationFrameRef.current = requestAnimationFrame(animateScroll);
  }, []);

  React.useEffect(() => {
    if (!showWidget) return;
    const timeoutId = window.setTimeout(() => smoothScrollToBottom(), 10);
    return () => window.clearTimeout(timeoutId);
  }, [showWidget, smoothScrollToBottom, widgetPrompt]);

  const smoothScrollToTop = React.useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    isAutoScrollingRef.current = true;

    // Cancel any existing animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startPosition = container.scrollTop;
    const targetPosition = 0;
    const distance = targetPosition - startPosition;
    const duration = 300; // ms
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out animation
      const easeOutProgress = 1 - Math.pow(1 - progress, 3);

      container.scrollTop = startPosition + distance * easeOutProgress;

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animateScroll);
      } else {
        isAutoScrollingRef.current = false;
        lastScrollPositionRef.current = container.scrollTop;
      }
    };

    animationFrameRef.current = requestAnimationFrame(animateScroll);
  }, []);

  // Enhanced auto-scroll for new messages with better conflict prevention
  React.useEffect(() => {
    if (displayMessages.length === 0) return;

    // Skip auto-scrolling when user has scrolled up
    if (userHasScrolledUp) {
      return;
    }

    // Clear any existing timeout
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
    }

    // Double-check scroll state before auto-scrolling to prevent conflicts
    autoScrollTimeoutRef.current = setTimeout(() => {
      // Final check: only auto-scroll if user hasn't scrolled up in the meantime
      if (!userHasScrolledUp && containerRef.current) {
        const container = containerRef.current;
        const maxScrollTop = container.scrollHeight - container.clientHeight;
        const currentScrollTop = container.scrollTop;

        // Only auto-scroll if we're reasonably close to bottom already
        const distanceFromBottom = maxScrollTop - currentScrollTop;
        if (distanceFromBottom < 200) {
          // Allow auto-scroll only if within 200px of bottom
          smoothScrollToBottom();
        }
      }
    }, 200); // Longer delay to ensure user actions are settled

    return () => {
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }
    };
  }, [displayMessages, userHasScrolledUp, smoothScrollToBottom, whiteboardSpec?.id]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (scrollDebounceTimeoutRef.current) {
        clearTimeout(scrollDebounceTimeoutRef.current);
      }
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current);
      }
    };
  }, []);

  // Enhanced scroll to bottom handler
  const handleScrollToBottom = React.useCallback(() => {
    smoothScrollToBottom();
  }, [smoothScrollToBottom]);

  const handleSendMessage = React.useCallback(async () => {
    const text = chatInput.trim();
    if (!room || !text) return;
    try {
      await sendTypedMessage(text);
      setChatInput("");
      setTimeout(() => smoothScrollToBottom(), 10);
    } catch (err) {
      logger.error("Failed to send text to LiveKit", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, [room, chatInput, smoothScrollToBottom, sendTypedMessage]);

  // Create & save a session summary from the transcript
  const handleCreateSummary = React.useCallback(async () => {
    try {
      setIsSummarizing(true);
      logger.info("Manual session summary requested", {
        currentConversationId,
        languageName: languageNameMemo,
        languageLevel: selectedLanguageLevel,
        sessionArtifactMessageCount: sessionArtifactMessages.length,
      });
      // Auth header for RLS insert
      const { data } = await supabase.auth.getSession();
      const access = data?.session?.access_token;
      const authHeaders: Record<string, string> = access
        ? { Authorization: `Bearer ${access}` }
        : {};

      // Prepare simplified messages (last 80)
      const recent = sessionArtifactMessages.slice(-80).map((seg) => ({
        role: seg.role,
        content: seg.text,
        timestamp: seg.firstReceivedTime,
      }));

      logger.info("Manual speaking memory upload started", {
        currentConversationId,
        sessionArtifactMessageCount: sessionArtifactMessages.length,
      });

      const speakingMemoryResult = await uploadRecentSpeakingMemories({
        conversationId: currentConversationId || "",
        languageName: languageNameMemo,
        languageLevel: selectedLanguageLevel,
        messages: sessionArtifactMessages as any,
        accessToken: access || undefined,
      });

      logger.info("Manual speaking memory upload finished", {
        uploaded: speakingMemoryResult.uploaded,
        currentConversationId,
      });

      const payload = {
        messages: recent,
        conversationId: currentConversationId,
        languageName: languageNameMemo,
        languageLevel: selectedLanguageLevel,
        sessionMode: isZenMode ? "zen" : "conversation",
        whiteboardResults: whiteboardResultsRef.current,
      };

      logger.info("Manual summary and diary POST started", {
        currentConversationId,
        messageCount: recent.length,
      });

      const [res, diaryRes] = await Promise.all([
        fetch("/api/progress/session-summary", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        }),
        fetch("/api/progress/tutor-diary", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        }),
      ]);

      logger.info("Manual summary and diary POST finished", {
        summaryStatus: res.status,
        diaryStatus: diaryRes.status,
        currentConversationId,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to create summary(${res.status})`);
      }

      const json = (await res.json()) as { summary?: string };
      const summary: string = json?.summary || "";
      if (!diaryRes.ok) {
        logger.warn("Tutor diary save failed (non-fatal)", { status: diaryRes.status });
      }
      if (summary) {
        setLastSummary(summary);
        setHasAvailableSummary(true);
        setIsSummaryModalOpen(true);
      }
    } catch (err) {
      logger.error("Failed to create session summary", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsSummarizing(false);
    }
  }, [
    allMessages,
    currentConversationId,
    languageNameMemo,
    selectedLanguageLevel,
    sessionArtifactMessages,
    uploadRecentSpeakingMemories,
  ]);

  // Format session duration
  const formatSessionTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Get language display name
  const getLanguageDisplayName = (code?: string): string => {
    if (!code) return "";
    return Object.entries(languages).find(([, c]) => c === code)?.[0] || code;
  };

  // Word click now shows action bar instead of flashcard directly
  const handleWordClick = React.useCallback(
    (word: string, position: WordActionAnchor) => {
      if (schoolMode) return;
      setWordTapHintDismissed(true);
      setActionBarWord(word);
      setActionBarPosition(position);
    },
    [schoolMode]
  );

  const handleCloseActionBar = React.useCallback(() => {
    setActionBarWord(null);
  }, []);

  const routesExerciseToTutorWhiteboard = shouldRouteExerciseToTutorWhiteboard({
    isActiveSession,
    isZenMode,
    tutorWhiteboardEnabled,
    hasRoom: Boolean(room),
  });
  const { selection: whiteboardSelection, selectForPractice } =
    useWhiteboardPracticeSelection(room);

  const handlePracticeFromMessage = React.useCallback(
    (
      messages: Array<{ role: string; text?: string; content?: string }>,
      messageIndex: number,
      messageKey: string
    ) => {
      if (routesExerciseToTutorWhiteboard) {
        const message = messages[messageIndex];
        void selectForPractice(messageKey, String(message?.text ?? message?.content ?? ""));
        return;
      }
      handleGenerateExerciseFromMessage(messages, messageIndex);
    },
    [handleGenerateExerciseFromMessage, selectForPractice, routesExerciseToTutorWhiteboard]
  );

  const handlePracticeConversation = React.useCallback(() => {
    if (routesExerciseToTutorWhiteboard) {
      void selectForPractice("conversation-exercise");
      return;
    }
    handleGenerateConversationExercise();
  }, [handleGenerateConversationExercise, selectForPractice, routesExerciseToTutorWhiteboard]);

  // "Ask Chicky" - send word to LiveKit tutor
  const handleAskChicky = React.useCallback(
    async (word: string) => {
      if (!room) return;
      try {
        const askMessage = `What does "${word}" mean? Can you explain it in context?`;
        await sendTypedMessage(askMessage);
        setTimeout(() => smoothScrollToBottom(), 10);
      } catch (err) {
        logger.error("Failed to send Ask Chicky message", {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [room, smoothScrollToBottom, sendTypedMessage]
  );

  // Open flashcard from action bar
  const handleOpenFlashcard = React.useCallback((word: string, position: WordActionAnchor) => {
    setSelectedSentence(word);
    setTooltipPosition(position);
  }, []);

  const handleCloseTooltip = React.useCallback(() => {
    setSelectedSentence(null);
  }, []);

  const handleSaveFlashcard = React.useCallback(
    async (sentence: string, data: FlashcardData) => {
      if (!languageNameMemo) return;

      await saveFlashcard(sentence, languageNameMemo, instructionLanguageMemo, data);
    },
    [instructionLanguageMemo, languageNameMemo, saveFlashcard]
  );

  const handleToggleFavorite = React.useCallback(
    async (sentence: string) => {
      if (!languageNameMemo) return;
      await toggleFavorite(sentence, languageNameMemo);
    },
    [languageNameMemo, toggleFavorite]
  );

  // Keyboard shortcuts for better UX
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!containerRef.current) return;

      switch (event.key) {
        case "End":
          event.preventDefault();
          // Scroll to bottom
          smoothScrollToBottom();
          break;
        case "Home":
          event.preventDefault();
          // Scroll to top
          smoothScrollToTop();
          break;
        case "Escape":
          // If any modal or overlay is open, close it instead of the session
          if (isSummaryModalOpen) {
            event.preventDefault();
            setIsSummaryModalOpen(false);
            return;
          }
          if (actionBarWord) {
            event.preventDefault();
            handleCloseActionBar();
            return;
          }
          if (selectedSentence) {
            event.preventDefault();
            handleCloseTooltip();
            return;
          }

          // Otherwise, close the transcription view/session
          if (trackedRecovery.onClose) {
            event.preventDefault();
            trackedRecovery.onClose();
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isSummaryModalOpen,
    actionBarWord,
    selectedSentence,
    trackedRecovery.onClose,
    smoothScrollToBottom,
    smoothScrollToTop,
    handleCloseActionBar,
    handleCloseTooltip,
  ]);

  const transcriptRootClass = isMathsWorkspace
    ? "flex h-full min-h-0 w-full overflow-hidden bg-[#0f0f14] text-white"
    : isZenMode
      ? "fixed inset-0 z-50 flex h-[100dvh] w-screen overflow-hidden bg-[#070807] text-white"
      : "fixed inset-0 z-50 flex h-[100dvh] w-screen overflow-hidden bg-[#0f0f14] text-white";
  const transcriptContentClass = isMathsWorkspace
    ? "w-full max-w-none px-4 sm:px-5 space-y-5"
    : isZenMode
      ? "w-full max-w-3xl lg:max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6"
      : "w-full max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6";
  const composerShellClass = isZenMode
    ? "sticky bottom-0 z-40 border-t border-white/10 bg-[#070807]/88 p-3 backdrop-blur-2xl"
    : "sticky bottom-0 z-40 border-t border-white/10 bg-[#0f0f14] p-3";
  const composerInputClass = isZenMode
    ? "h-11 w-full rounded-full border border-white/10 bg-white/[0.07] px-4 text-[15px] leading-normal text-white placeholder:text-white/35 transition-colors duration-200 focus:border-teal-100/35 focus:outline-none focus:ring-2 focus:ring-teal-100/20"
    : "h-11 w-full rounded-md border border-white/10 bg-white/[0.045] px-4 text-[15px] leading-normal text-white placeholder:text-white/30 transition-colors duration-200 focus:border-orange-300/45 focus:outline-none focus:ring-2 focus:ring-orange-300/20";
  const composerButtonClass = isZenMode
    ? "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/90 text-stone-950 shadow-[0_16px_38px_rgba(0,0,0,0.25)] transition-all duration-200 hover:scale-105 hover:bg-white disabled:bg-white/10 disabled:text-white/30 disabled:shadow-none disabled:hover:scale-100"
    : "flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-orange-500 text-white transition-colors duration-200 hover:bg-orange-400 disabled:bg-white/[0.06] disabled:text-white/25";
  const tutorSpeedControl =
    ttsRuntimeControls.speakingRate && ttsRuntimeControls.previewValue !== null ? (
      <TutorSpeedDial
        min={ttsRuntimeControls.speakingRate.min}
        max={ttsRuntimeControls.speakingRate.max}
        step={ttsRuntimeControls.speakingRate.step}
        value={ttsRuntimeControls.previewValue}
        isPending={ttsRuntimeControls.isPending}
        error={ttsRuntimeControls.error}
        onPreview={ttsRuntimeControls.previewSpeakingRate}
        onCommit={ttsRuntimeControls.commitSpeakingRate}
      />
    ) : null;

  return (
    <div
      className={`${transcriptRootClass} ${!isZenMode && !schoolMode && !isMathsWorkspace ? workspaceStyles.workspace : ""}`}
    >
      {isZenMode ? (
        <>
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(153,246,228,0.13),transparent_32%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.08),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.03),transparent_38%)]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 to-transparent" />
        </>
      ) : null}

      {/* Sidebar removed in this view */}

      {/* Main Transcription Area - fills the rest */}
      <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
        {/* Session Header */}
        {showSessionHeader && (showHeaderWhenInactive || isActiveSession) && (
          <SessionHeader
            room={room}
            isActiveSession={isActiveSession}
            sessionDuration={sessionDuration}
            selectedLanguageCode={selectedLanguageCode}
            selectedLanguageLevel={selectedLanguageLevel}
            showLanguageLevel={!schoolMode}
            isMuted={isMuted}
            onToggleMute={voiceNote.isActive ? undefined : onToggleMute}
            onClose={trackedRecovery.onClose}
            getLanguageDisplayName={getLanguageDisplayName}
            formatSessionTime={formatSessionTime}
            isZenMode={isZenMode}
            assistantDisplayName={assistantDisplayName}
            disableAvatar={disableAvatar}
            renderRoomAudio={renderRoomAudio}
            sessionControls={sessionControlsPortalId ? undefined : tutorSpeedControl || undefined}
          />
        )}

        {learningMode === "guided" &&
          sessionMode === "conversation" &&
          isActiveSession &&
          hasFinalTutorTurn &&
          !isZenMode &&
          !schoolMode && (
            <LearningModeTransitionControl
              targetLanguage={languageNameMemo || selectedLanguageCode || "the target language"}
              state={learningModeTransition.state}
              error={learningModeTransition.error}
              onSwitch={() => void learningModeTransition.switchToConversation()}
            />
          )}

        {translationFeatureAvailable &&
          isActiveSession &&
          hasFinalTutorTurn &&
          translationLanguageOptions.length > 0 && (
            <TranslationControls
              enabled={translationsEnabled}
              languageCode={effectiveTranslationLanguageCode}
              languageOptions={translationLanguageOptions}
              onEnabledChange={setTranslationsEnabled}
              onLanguageChange={setTranslationLanguageCode}
            />
          )}

        {/* Messages Area - ChatGPT Style */}
        <div
          ref={containerRef}
          data-transcript-scroll
          className="flex-1 overflow-y-auto pt-6 overscroll-contain relative min-h-0 max-h-full pb-24 sm:pb-28"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {/* Content container focused on optimal text readability */}
          <div
            data-transcript-content
            className={`${transcriptContentClass} ${
              isActiveSession && displayMessages.length === 0 ? "flex min-h-full flex-col" : ""
            }`}
          >
            {isActiveSession ? (
              <ContinuationContext
                messages={previousTutorMessages}
                assistantLabel={assistantDisplayName || "Tutor"}
              />
            ) : null}
            {recoveryKind === "connection" ? (
              <ConnectionStatus
                connectionState={connectionState}
                connectionError={connectionError}
                retryConnection={trackedRecovery.retryConnection}
              />
            ) : isActiveSession ? (
              <>
                {recoveryKind === "audio" && (
                  <AudioTrackError
                    audioTrackError={audioTrackError}
                    audioRetryCount={audioRetryCount}
                    retryAudioTrack={trackedRecovery.retryAudioTrack}
                    clearAudioErrors={clearAudioErrors}
                  />
                )}
                <AnimatePresence mode="wait" initial={false}>
                  {displayMessages.length === 0 ? (
                    <motion.div
                      key="active-empty-state"
                      className="flex min-h-0 w-full flex-1"
                      exit={{ filter: "blur(4px)", opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.24, ease: "easeOut" }}
                    >
                      <EmptyState
                        onViewSummary={() => setIsSummaryModalOpen(true)}
                        hasSummary={hasAvailableSummary}
                        isZenMode={isZenMode}
                        agentState={agentState}
                        interactiveChicky={!isZenMode && !schoolMode}
                        fillAvailableHeight
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="active-transcript-messages"
                      className="w-full"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.28, ease: "easeOut" }}
                    >
                      <AnimatePresence initial={false}>
                        {displayMessages.map((segment: any, index: number) => {
                          const isLastMessage = index === displayMessages.length - 1;
                          const isAssistantMessage = segment.role === "assistant";
                          const showWordTapHint =
                            !schoolMode &&
                            !wordTapHintDismissed &&
                            isAssistantMessage &&
                            index === firstAssistantMessageIndex;
                          const messageKey = String(
                            segment.id ??
                              `${segment.role}_${segment.firstReceivedTime ?? "message"}_${index}`
                          );
                          // Keep the local selection acknowledgement visible while the tutor
                          // changes state; its duration does not follow generation/delivery.
                          const showAssistantMessageActions =
                            (isAssistantMessage && whiteboardSelection?.targetKey === messageKey) ||
                            shouldShowAssistantMessageActions(
                              segment.role as "assistant" | "user",
                              segment.text,
                              {
                                isLastMessage,
                                agentState:
                                  isLastMessage && isAssistantMessage ? agentState : undefined,
                                isFinal: isAssistantMessage
                                  ? resolveTranscriptionFinality(segment)
                                  : undefined,
                              }
                            );
                          const showUserMessageActions = shouldShowUserMessageActions(
                            segment.role as "assistant" | "user",
                            segment.text
                          );
                          const tutorTranslation =
                            translationsActive && isAssistantMessage
                              ? getTutorTurnTranslation(segment as TutorTranscriptSegment)
                              : undefined;
                          const replaySource = transcriptReplaySource(
                            allMessages,
                            displayMessages,
                            index
                          );
                          const sourceIndex = replaySource.sourceIndex;
                          const replayOccurrence =
                            isAssistantMessage && sourceIndex >= 0
                              ? tutorReplayOccurrence(allMessages, sourceIndex)
                              : 0;
                          const replayText = replaySource.text;
                          const replayToken = isAssistantMessage
                            ? tutorReplayMessageToken(replayText, replayOccurrence)
                            : "";
                          const replayId = tutorReplayMessageIds.get(replayToken);
                          const replayAvailable = Boolean(
                            replaySource.isLastFragment &&
                            replayId &&
                            tutorReplay.isReplayAvailable(replayId)
                          );
                          const replayState =
                            tutorReplay.preparingReplayId === replayToken
                              ? "preparing"
                              : tutorReplay.playingReplayId === replayToken
                                ? "playing"
                                : "idle";

                          const messageNode = (
                            <TranscriptionMessage
                              id={segment.id}
                              role={segment.role as "assistant" | "user"}
                              content={segment.text}
                              isFinal={
                                isAssistantMessage ? resolveTranscriptionFinality(segment) : true
                              }
                              assistantLabel={assistantDisplayName}
                              isLastMessage={isLastMessage}
                              showLivePresence={!isZenMode && !schoolMode && !isMathsWorkspace}
                              microphoneRoom={room}
                              microphoneMuted={isMuted}
                              agentState={
                                isLastMessage && isAssistantMessage
                                  ? (agentState as SessionAgentState)
                                  : undefined
                              }
                              showWordTapHint={showWordTapHint}
                              ref={isLastMessage ? lastMessageRef : undefined}
                              isZenMode={isZenMode}
                              translation={tutorTranslation?.translation}
                              translationLanguage={tutorTranslation?.translationLanguage}
                              translationStatus={tutorTranslation?.status}
                              onRetryTranslation={
                                tutorTranslation?.status === "error"
                                  ? () => retryTranslation(segment as TutorTranscriptSegment)
                                  : undefined
                              }
                              actions={
                                !schoolMode && showAssistantMessageActions ? (
                                  <MessageBubbleActions
                                    whiteboardSelectionId={
                                      whiteboardSelection?.targetKey === messageKey
                                        ? whiteboardSelection.id
                                        : undefined
                                    }
                                    isLoading={isSentenceMapLoading}
                                    isCopied={copiedMessageKey === messageKey}
                                    isZenMode={isZenMode}
                                    onOpenSentenceBridge={() =>
                                      handleGenerateSentenceMapFromMessage(
                                        displayMessages,
                                        index,
                                        messageKey
                                      )
                                    }
                                    onGenerateExercise={() =>
                                      handlePracticeFromMessage(displayMessages, index, messageKey)
                                    }
                                    onCopy={() =>
                                      handleCopyTranscriptMessage(String(segment.text), messageKey)
                                    }
                                    onReplay={
                                      tutorReplay.isSupported && replayAvailable
                                        ? () =>
                                            void tutorReplay.replayTutorMessage(
                                              replayText,
                                              replayOccurrence
                                            )
                                        : undefined
                                    }
                                    replayWholeResponse={replaySource.wholeResponse}
                                    replayState={replayState}
                                    replayDisabled={
                                      agentState !== "listening" ||
                                      (Boolean(tutorReplay.playingReplayId) &&
                                        replayState !== "playing") ||
                                      (Boolean(tutorReplay.preparingReplayId) &&
                                        replayState !== "preparing")
                                    }
                                    replaySource={
                                      replayState === "playing" ? tutorReplay.playingSource : null
                                    }
                                  />
                                ) : !schoolMode && showUserMessageActions ? (
                                  <UserMessageActionsMenu
                                    whiteboardSelectionId={
                                      whiteboardSelection?.targetKey === messageKey
                                        ? whiteboardSelection.id
                                        : undefined
                                    }
                                    isLoading={isSentenceMapLoading}
                                    isCopied={copiedMessageKey === messageKey}
                                    isZenMode={isZenMode}
                                    onOpenSentenceBridge={() =>
                                      handleGenerateSentenceMapFromMessage(
                                        displayMessages,
                                        index,
                                        messageKey
                                      )
                                    }
                                    onGenerateExercise={() =>
                                      handlePracticeFromMessage(displayMessages, index, messageKey)
                                    }
                                    onCopy={() =>
                                      handleCopyTranscriptMessage(String(segment.text), messageKey)
                                    }
                                  />
                                ) : undefined
                              }
                            />
                          );

                          return schoolMode ? (
                            <motion.div
                              key={segment.id ?? `${segment.role}_${index}`}
                              initial={{ opacity: 0, y: 10, scale: 0.95 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              transition={{ type: "spring", stiffness: 400, damping: 25 }}
                            >
                              {messageNode}
                            </motion.div>
                          ) : (
                            <motion.div
                              key={segment.id ?? `${segment.role}_${index}`}
                              initial={{ opacity: 0, y: 10, scale: 0.95 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              transition={{ type: "spring", stiffness: 400, damping: 25 }}
                            >
                              <SelectableText
                                onWordClick={handleWordClick}
                                targetLanguage={languageNameMemo}
                              >
                                {messageNode}
                              </SelectableText>
                              {sentenceMapAnchorKey === messageKey && sentenceMapBoard}
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>

                      {/* Padding at bottom */}
                      <div className="h-4"></div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {tutorReplay.error ? (
                  <p role="alert" className="mt-2 text-center text-xs font-medium text-rose-300/90">
                    {tutorReplay.error}
                  </p>
                ) : null}

                {/* The active tutor board is part of the conversation flow.
                    Focus mode changes this same mounted surface's layout, so
                    learner progress survives expanding and returning to chat. */}
                {!isZenMode && tutorWhiteboardEnabled && !whiteboardPortalTarget && (
                  <WhiteboardConversationSurface
                    spec={whiteboardSpec}
                    renderState={whiteboardRenderState}
                    onEvent={sendWhiteboardEvent}
                    onPresentationChange={reportWhiteboardPresentation}
                    focused={isWhiteboardFocused}
                    tutorRevision={whiteboardTutorRevision}
                    focusOnTutorRevision={isMobileWhiteboardViewport}
                    minimizedByAgent={whiteboardMinimizedByAgent}
                    onFocusedChange={setIsWhiteboardFocused}
                    onRestore={restoreWhiteboard}
                  />
                )}
              </>
            ) : (
              <>
                {recoveryKind === "audio" && (
                  <AudioTrackError
                    audioTrackError={audioTrackError}
                    audioRetryCount={audioRetryCount}
                    retryAudioTrack={trackedRecovery.retryAudioTrack}
                    clearAudioErrors={clearAudioErrors}
                  />
                )}
                {/* Board recap: boards from the ended session become tappable
                    flashcards instead of vanishing with the room. */}
                {!isZenMode && !schoolMode && whiteboardBoardHistory.length > 0 && (
                  <WhiteboardRecapGallery
                    boards={whiteboardBoardHistory}
                    targetLanguage={languageNameMemo}
                    instructionLanguage={instructionLanguageMemo}
                    languageLevel={selectedLanguageLevel}
                    userId={user?.id}
                    isFlashcardSaved={isFlashcardSaved}
                    saveFlashcard={saveFlashcard}
                  />
                )}
                {!currentConversation || (currentConversation.messages?.length || 0) === 0 ? (
                  showReadyStateWhenInactive ? (
                    <EmptyState
                      onViewSummary={() => setIsSummaryModalOpen(true)}
                      hasSummary={hasAvailableSummary}
                      isZenMode={isZenMode}
                      agentState={agentState}
                    />
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-lg font-bold text-[#F9F9F9] mb-2">No messages yet</div>
                      <div className="text-sm text-[#AEAEB2] leading-relaxed">
                        Select another conversation or start a new session.
                      </div>
                    </div>
                  )
                ) : (
                  <AnimatePresence initial={false}>
                    {currentConversation.messages.map((m, idx) => {
                      const messageKey = `history_${idx}_${m.timestamp ?? "message"}`;
                      const conversationMessages = currentConversation.messages || [];
                      const showAssistantMessageActions = shouldShowAssistantMessageActions(
                        m.role as "assistant" | "user",
                        m.content
                      );
                      const showUserMessageActions = shouldShowUserMessageActions(
                        m.role as "assistant" | "user",
                        m.content
                      );
                      const messageNode = (
                        <TranscriptionMessage
                          role={m.role as "assistant" | "user"}
                          content={m.content}
                          assistantLabel={assistantDisplayName}
                          isZenMode={isZenMode}
                          actions={
                            !schoolMode && showAssistantMessageActions ? (
                              <MessageBubbleActions
                                whiteboardSelectionId={
                                  whiteboardSelection?.targetKey === messageKey
                                    ? whiteboardSelection.id
                                    : undefined
                                }
                                isLoading={isSentenceMapLoading}
                                isCopied={copiedMessageKey === messageKey}
                                isZenMode={isZenMode}
                                onOpenSentenceBridge={() =>
                                  handleGenerateSentenceMapFromMessage(
                                    conversationMessages,
                                    idx,
                                    messageKey
                                  )
                                }
                                onGenerateExercise={() =>
                                  handlePracticeFromMessage(conversationMessages, idx, messageKey)
                                }
                                onCopy={() => handleCopyTranscriptMessage(m.content, messageKey)}
                              />
                            ) : !schoolMode && showUserMessageActions ? (
                              <UserMessageActionsMenu
                                whiteboardSelectionId={
                                  whiteboardSelection?.targetKey === messageKey
                                    ? whiteboardSelection.id
                                    : undefined
                                }
                                isLoading={isSentenceMapLoading}
                                isCopied={copiedMessageKey === messageKey}
                                isZenMode={isZenMode}
                                onOpenSentenceBridge={() =>
                                  handleGenerateSentenceMapFromMessage(
                                    conversationMessages,
                                    idx,
                                    messageKey
                                  )
                                }
                                onGenerateExercise={() =>
                                  handlePracticeFromMessage(conversationMessages, idx, messageKey)
                                }
                                onCopy={() => handleCopyTranscriptMessage(m.content, messageKey)}
                              />
                            ) : undefined
                          }
                        />
                      );

                      return schoolMode ? (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ type: "spring", stiffness: 400, damping: 25 }}
                        >
                          {messageNode}
                        </motion.div>
                      ) : (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ type: "spring", stiffness: 400, damping: 25 }}
                        >
                          <SelectableText
                            onWordClick={handleWordClick}
                            targetLanguage={languageNameMemo}
                          >
                            {messageNode}
                          </SelectableText>
                          {sentenceMapAnchorKey === messageKey && sentenceMapBoard}
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                )}
              </>
            )}
            {!sentenceMapAnchorKey && sentenceMapBoard}
            {showWidget && (
              <InteractiveWidget
                prompt={widgetPrompt}
                isLoading={isWidgetLoading}
                error={widgetError}
                onClose={closeWidget}
              />
            )}
            {/* Scroll buttons */}
            <ScrollToBottomButton
              show={isActiveSession && showScrollToBottom && displayMessages.length > 0}
              onScrollToBottom={handleScrollToBottom}
              onScrollToTop={smoothScrollToTop}
            />
          </div>
        </div>

        {isActiveSession && (
          <div
            data-transcript-composer
            className={composerShellClass}
            style={{ paddingBottom: "max(0.75rem, calc(env(safe-area-inset-bottom) + 0.5rem))" }}
          >
            {voiceNote.pronunciationAnalysis ? (
              <div className="mx-auto mb-2 w-full max-w-2xl px-4 sm:px-6 lg:max-w-3xl lg:px-8 xl:max-w-4xl">
                <PronunciationAnalysis
                  result={voiceNote.pronunciationAnalysis}
                  onDismiss={voiceNote.dismissPronunciationAnalysis}
                />
              </div>
            ) : null}
            <div
              data-transcript-composer-dock
              className="w-full max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-2"
            >
              {showVoiceNotes && voiceNote.state !== "idle" ? (
                <VoiceNoteComposer
                  state={voiceNote.state}
                  elapsedSeconds={voiceNote.elapsedSeconds}
                  error={voiceNote.error}
                  audioLevel={voiceNote.audioLevel}
                  available={voiceNote.isAvailable}
                  onStart={voiceNote.start}
                  onCommit={voiceNote.commit}
                  onCancel={voiceNote.cancel}
                  onReset={voiceNote.reset}
                />
              ) : (
                <>
                  <TranscriptTextInput
                    value={chatInput}
                    onChange={setChatInput}
                    onSend={handleSendMessage}
                    disabled={!room}
                    placeholder={
                      isZenMode ? "Share a reflection..." : "Type a message... (Enter to send)"
                    }
                    inputClassName={composerInputClass}
                    buttonClassName={composerButtonClass}
                  />
                  {showVoiceNotes && !isZenMode && !schoolMode && (
                    <VoiceNoteComposer
                      state={voiceNote.state}
                      elapsedSeconds={voiceNote.elapsedSeconds}
                      error={voiceNote.error}
                      audioLevel={voiceNote.audioLevel}
                      available={voiceNote.isAvailable}
                      onStart={voiceNote.start}
                      onCommit={voiceNote.commit}
                      onCancel={voiceNote.cancel}
                      onReset={voiceNote.reset}
                    />
                  )}
                  {!schoolMode && (
                    <PlusOptionsMenu
                      whiteboardSelectionId={
                        whiteboardSelection?.targetKey === "conversation-exercise"
                          ? whiteboardSelection.id
                          : undefined
                      }
                      onSummary={handleCreateSummary}
                      isSummarizing={isSummarizing}
                      onViewSummary={handleViewSummary}
                      onGenerateExercise={handlePracticeConversation}
                      onGiveHint={() =>
                        generateFlowCanvas(
                          allMessages.map((m: any) => ({ role: m.role, content: m.text })),
                          languageNameMemo || "English",
                          selectedLanguageLevel
                        )
                      }
                      onGenerateSentenceMap={() =>
                        generateSentenceMap(
                          sessionArtifactMessages.map((m) => ({
                            role: m.role,
                            content: m.text,
                          })),
                          languageNameMemo || "English",
                          sentenceMapInstructionLanguage,
                          selectedLanguageLevel
                        )
                      }
                      pronunciationAnalysisAvailable={voiceNote.pronunciationAnalysisAvailable}
                      pronunciationAnalysisEnabled={voiceNote.pronunciationAnalysisEnabled}
                      onPronunciationAnalysisEnabledChange={
                        voiceNote.setPronunciationAnalysisEnabled
                      }
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {sessionControlsPortalTarget && tutorSpeedControl
        ? createPortal(tutorSpeedControl, sessionControlsPortalTarget)
        : null}

      {whiteboardPortalTarget && tutorWhiteboardEnabled
        ? createPortal(
            <WhiteboardConversationSurface
              spec={whiteboardSpec}
              renderState={whiteboardRenderState}
              onEvent={sendWhiteboardEvent}
              onPresentationChange={reportWhiteboardPresentation}
              focused={false}
              tutorRevision={whiteboardTutorRevision}
              minimizedByAgent={false}
              onFocusedChange={() => undefined}
              onRestore={restoreWhiteboard}
              presentation="workspace"
              ariaLabel="Interactive Maths equation workspace"
            />,
            whiteboardPortalTarget
          )
        : null}

      {/* Session Summary Modal */}
      <Dialog open={isSummaryModalOpen} onOpenChange={setIsSummaryModalOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl gap-0 overflow-y-auto border-white/10 bg-[#121318] p-0 shadow-2xl shadow-black/45">
          <div
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Escape") {
                e.stopPropagation();
              }
            }}
          >
            <DialogHeader className="sticky top-0 z-10 border-b border-white/10 bg-[#121318] p-5 sm:p-6">
              <DialogTitle className="flex items-center gap-3 text-xl font-bold text-white">
                <BookOpen className="h-5 w-5 text-orange-300" />
                Last session summary
              </DialogTitle>
            </DialogHeader>

            <div className="p-6 overflow-y-auto custom-scrollbar">
              {lastSummary ? (
                <div className="prose prose-invert prose-orange max-w-none">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => (
                        <h1 className="text-xl font-bold text-[#FFE4CC] mb-4 flex items-center gap-2 border-b border-[#FD954F]/20 pb-2">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-lg font-bold text-[#FD954F] mt-6 mb-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-[#FD954F]" />
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-base font-bold text-[#FD954F]/90 mt-4 mb-2">
                          {children}
                        </h3>
                      ),
                      ul: ({ children }) => (
                        <ul className="space-y-2 my-4 list-none pl-1">{children}</ul>
                      ),
                      li: ({ children }) => (
                        <li className="flex gap-2 text-[#AEAEB2] leading-relaxed text-[15px]">
                          <span className="text-[#FD954F]/60 mt-1.5">•</span>
                          <span>{children}</span>
                        </li>
                      ),
                      strong: ({ children }) => (
                        <strong className="text-[#FD954F] font-semibold">{children}</strong>
                      ),
                      p: ({ children }) => (
                        <p className="text-[#AEAEB2] leading-relaxed mb-4 text-[15px]">
                          {children}
                        </p>
                      ),
                    }}
                  >
                    {lastSummary}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-[#AEAEB2] gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FD954F]"></div>
                  <p>Loading summary...</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Word Action Bar - appears on word click */}
      {!schoolMode && actionBarWord && (
        <WordActionBar
          word={actionBarWord}
          position={actionBarPosition}
          onAskChicky={handleAskChicky}
          onOpenFlashcard={handleOpenFlashcard}
          onClose={handleCloseActionBar}
        />
      )}

      {/* Flashcard Tooltip */}
      {!schoolMode && selectedSentence && languageNameMemo && (
        <FlashcardTooltip
          key={`${selectedLanguageCode}:${effectiveTranslationLanguageCode}:${selectedSentence}`}
          sentence={selectedSentence}
          position={tooltipPosition}
          onClose={handleCloseTooltip}
          targetLanguage={languageNameMemo}
          instructionLanguage={instructionLanguageMemo}
          languageLevel={selectedLanguageLevel}
          userId={user?.id}
          onSave={handleSaveFlashcard}
          onFavorite={handleToggleFavorite}
          isFavorited={isFlashcardFavorited(selectedSentence, languageNameMemo)}
          isSaved={isFlashcardSaved(selectedSentence, languageNameMemo)}
        />
      )}

      {/* Flow Canvas Widget for conversation hints */}
      {!schoolMode && showFlowCanvas && (
        <FlowCanvasWidget
          messages={flowCanvasMessages}
          isLoading={isFlowCanvasLoading}
          error={flowCanvasError}
          onClose={closeFlowCanvas}
          onSpeakText={handleSpeakText}
          onSelectPath={handleSelectPath}
        />
      )}

      {/* Semantic Branching Canvas - Smart Learning Canvas */}
      {/* Hidden on mobile to prevent obstructing transcript view */}
      {/* On mobile, learning features are accessible via the message tools menu */}
      {!schoolMode && isActiveSession && !isMobileDevice && (
        <BranchingCanvas
          nodes={branchingCanvas.recentNodes}
          grammarStats={branchingCanvas.grammarStats}
          suggestedQuestions={branchingCanvas.suggestedQuestions}
          weakAreas={branchingCanvas.weakAreas}
          isLoading={branchingCanvas.isLoading}
          isExpanded={branchingCanvas.isExpanded}
          adaptationResult={branchingCanvas.adaptationResult}
          onToggleExpand={branchingCanvas.toggleExpand}
          onNodeSelect={branchingCanvas.handleNodeSelect}
          onAdaptPhrase={(node) => branchingCanvas.handleAdaptPhrase(node)}
          onClearAdaptation={branchingCanvas.clearAdaptationResult}
          onSendQuestion={handleSelectPath}
          selectedNodeId={branchingCanvas.selectedNodeId}
          messageCount={branchingCanvas.messageCount}
        />
      )}
    </div>
  );
}
